// Worksheet (No Fuss Tutors) users. Uses the paginated admin endpoints on nftserver and falls back to
// the older POST /api/users/recent (server paged, no search or filters) until the server update ships.
import { ApiError, getDashboardToken, nftUrl, requestJSON } from './api';
import { Paged, WorksheetUserDetail, WorksheetUserRow, WorksheetUserStats } from '../Models/AdminUsers';

export type WorksheetUserQuery = {
  page: number;
  limit: number;
  search: string;
  activity: 'all' | 'with' | 'without';
  plan: string;
  from: string;
  to: string;
  sort: string;
  dir: 'asc' | 'desc';
  site?: string;
};

export type WorksheetUserList = Paged<WorksheetUserRow> & { legacy?: boolean };

let legacyMode = false;
export const authHeaders = (): Record<string, string> => {
  const token = getDashboardToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const isLegacyMode = () => legacyMode;

const legacyRaw = new Map<string, any>();

const planOf = (user: any) =>
  user.customer_id && user.subscription_id && user.subscription_active
    ? 'paid'
    : user.subscription_active && user.userRole === 'Tutor'
    ? 'tutor'
    : user.subscription_active
    ? 'client'
    : 'free';

const lastDownload = (user: any) => {
  const dates = [...(user.worksheets || []), ...(user.assessments || [])]
    .map((doc: any) => new Date(doc.date_downloaded).valueOf())
    .filter((value: number) => !isNaN(value));
  return dates.length ? new Date(Math.max(...dates)).toISOString() : null;
};

const fromLegacy = (user: any): WorksheetUserRow => {
  legacyRaw.set(user._id, user);
  return {
    _id: user._id,
    email: (user.user_data && user.user_data.email) || '',
    name: user.user_data && user.user_data.full_name,
    phone: user.user_data && user.user_data.phone,
    role: user.userRole,
    tutorInterest: user.tutorInterest,
    plan: planOf(user),
    createdAt: user.date_created || null,
    lastActivity: lastDownload(user),
    worksheetCount: (user.worksheets || []).length,
    assessmentCount: (user.assessments || []).length,
  };
};

const fetchLegacyList = async (query: WorksheetUserQuery, signal: AbortSignal): Promise<WorksheetUserList> => {
  const limit = Math.min(query.limit, 200);
  const result = await requestJSON<any>(nftUrl('/api/users/recent'), {
    headers: authHeaders(),
    method: 'POST',
    body: JSON.stringify({ page: query.page, limit }),
    signal,
  });
  const without = query.activity === 'without';
  const users = (without ? result.usersWithoutWorksheets : result.sortedUsersWithWorksheets) || [];
  const total = without ? result.totalWithout : result.totalWith;
  return { users: users.map(fromLegacy), total, page: query.page, limit, totalPages: Math.max(1, Math.ceil(total / limit)), legacy: true };
};

export const fetchWorksheetUsers = async (query: WorksheetUserQuery, signal: AbortSignal): Promise<WorksheetUserList> => {
  if (!legacyMode) {
    try {
      return await requestJSON<WorksheetUserList>(nftUrl('/api/users/admin/list', query), { signal, headers: authHeaders() });
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 404)) throw err;
      legacyMode = true;
    }
  }
  return fetchLegacyList(query, signal);
};

export const fetchWorksheetStats = async (signal: AbortSignal): Promise<Partial<WorksheetUserStats> & { legacy?: boolean }> => {
  try {
    return await requestJSON<WorksheetUserStats>(nftUrl('/api/users/admin/stats'), { signal, headers: authHeaders() });
  } catch (err) {
    if (!(err instanceof ApiError && err.status === 404)) throw err;
    const [count, recent] = await Promise.all([
      requestJSON<{ total: number }>(nftUrl('/api/users/count'), { signal }),
      requestJSON<any>(nftUrl('/api/users/recent'), { method: 'POST', body: JSON.stringify({ page: 1, limit: 1 }), signal, headers: authHeaders() }),
    ]);
    return { total: count.total, withActivity: recent.totalWith, withoutActivity: recent.totalWithout, legacy: true };
  }
};

let resourceTitles: Promise<Map<string, string>> | null = null;
const legacyTitles = () => {
  if (!resourceTitles) {
    resourceTitles = requestJSON<any>(`${process.env.REACT_APP_BASE_URL}/api/resources/fetchV3resources`).then(
      (response) => new Map<string, string>((response.resources || []).map((r: any) => [String(r._id), r.title])),
    );
    resourceTitles.catch(() => {
      resourceTitles = null;
    });
  }
  return resourceTitles;
};

export const fetchWorksheetUser = async (id: string, signal: AbortSignal): Promise<WorksheetUserDetail> => {
  const raw = legacyRaw.get(id);
  if (legacyMode && raw) {
    const titles = await legacyTitles();
    const downloads = [
      ...(raw.worksheets || []).map((doc: any) => ({ type: 'worksheet' as const, ...doc })),
      ...(raw.assessments || []).map((doc: any) => ({ type: 'assessment' as const, ...doc })),
    ]
      .map((doc: any) => ({ type: doc.type, resourceId: doc.resource_id, title: titles.get(String(doc.resource_id)) || null, date: doc.date_downloaded || null }))
      .sort((a, b) => new Date(b.date || 0).valueOf() - new Date(a.date || 0).valueOf());
    return { ...fromLegacy(raw), downloads };
  }
  const result = await requestJSON<{ user: WorksheetUserDetail }>(nftUrl(`/api/users/admin/${id}`), { signal, headers: authHeaders() });
  return result.user;
};

/** Downloads the filtered user list as CSV (needs the dashboard pass, so it is fetched, not linked). */
export const downloadWorksheetExport = async (query: Omit<WorksheetUserQuery, 'page' | 'limit'>) => {
  const response = await fetch(nftUrl('/api/users/admin/export', query), { headers: authHeaders() });
  if (!response.ok) throw new ApiError(response.status === 401 ? 'Please sign in to the dashboard again.' : 'Export failed.', response.status);
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = `worksheet-users-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const giveUserPaidSub = (email: string) =>
  requestJSON<{ message: string }>(nftUrl('/api/users/giveUserPaidSub'), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ email }),
  });
